import React from 'react';
import {
  HealthCheckSeverityLabel,
  resolveHealthCheckScorePresentation,
} from '../Onboarding/ContentInventory/Components/HealthCheckScorePresentation';

/**
 * The fill of a health meter per severity band — the bands the check badges use, so a red meter and
 * a red badge mean the same thing, in solid colours that stay clear at the meter's small size.
 */
export const HEALTH_METER_FILL_CLASSES: Record<HealthCheckSeverityLabel, string> = {
  Critical: 'bg-rose-500',
  High: 'bg-orange-500',
  Medium: 'bg-amber-400',
  Low: 'bg-emerald-500',
};

/**
 * A soft tint per severity band for a cell whose figure sits on it: the meter's hues, pale enough
 * for dark text in the same hue to read on them.
 */
export const HEALTH_TINT_CLASSES: Record<HealthCheckSeverityLabel, string> = {
  Critical: 'bg-rose-100/80 text-rose-800',
  High: 'bg-orange-100/80 text-orange-800',
  Medium: 'bg-amber-100/80 text-amber-900',
  Low: 'bg-emerald-100/80 text-emerald-800',
};

/** A page's health as a figure over a short meter coloured by its severity band. */
export function HealthScoreCell({ score }: { score?: number }) {
  if (score === undefined) {
    return <span className="text-sm text-slate-400">—</span>;
  }

  const { label } = resolveHealthCheckScorePresentation(score);

  return (
    <div className="flex flex-col gap-1.5" title={`${score}% health score (${label})`}>
      <span className="text-sm font-bold tabular-nums text-slate-900">{score}%</span>
      <span aria-hidden="true" className="block h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
        {/* The width is the score itself, known only at runtime. */}
        <span
          className={`block h-full rounded-full transition-[width] duration-700 ease-out ${HEALTH_METER_FILL_CLASSES[label]}`}
          style={{ width: `${score}%` }}
        />
      </span>
    </div>
  );
}
